import { Badge } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { Icono } from '@/components/Icono';
import type { EstadoDeSolicitud, Solicitud } from './api';

/**
 * piezas.tsx — lo que comparten la bandeja de gestión y «mis solicitudes».
 *
 * El tono del estado sale de los tokens. El vanilla lo llevaba en una tabla de
 * `rgba()` literales, y su propio comentario explicaba por qué no podían ser
 * variables: «la plantilla le concatena alfa y `var(--x)1a` no es CSS válido».
 * Ese apaño es el mismo que dejó las alertas de la Analítica sin fondo durante
 * meses — aquí el tinte lo pone `Badge`, que no concatena nada.
 */

const TONOS: Record<EstadoDeSolicitud, 'info' | 'warn' | 'ok' | 'danger' | 'neutral'> = {
  enviada: 'info',
  en_revision: 'warn',
  aprobada: 'ok',
  rechazada: 'danger',
  respondida: 'neutral',
};

export function InsigniaDeEstado({ solicitud }: { solicitud: Solicitud }) {
  return (
    <Badge tone={TONOS[solicitud.status] ?? 'neutral'}>
      {solicitud.status_label || solicitud.status}
    </Badge>
  );
}

/** Una fila de la lista de radicados. Abre el detalle. */
export function FilaDeSolicitud({
  solicitud, conAutor, onAbrir,
}: {
  solicitud: Solicitud;
  /** En la bandeja de gestión se ve quién radicó; en «mis solicitudes» no. */
  conAutor?: boolean;
  onAbrir: () => void;
}) {
  const fecha = solicitud.created_at ? Utils.fmtDateOnly(solicitud.created_at) : '';
  return (
    <Card interactiva>
      <button
        type="button"
        className="flex w-full items-center gap-3 p-3 text-left focus-visible:focus-anillo"
        onClick={onAbrir}
      >
        <span className="shrink-0 text-7 text-brand-ink" aria-hidden="true">
          <Icono nombre={solicitud.icon} respaldo="bi-file-earmark-text" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-3 font-semibold text-ink">
            {solicitud.template_name}
          </span>
          <span className="block truncate text-1 text-muted">
            {solicitud.radicado}
            {conAutor && solicitud.user_name ? ` · ${solicitud.user_name} (${solicitud.area})` : ''}
            {fecha ? ` · ${fecha}` : ''}
          </span>
        </span>
        {solicitud.has_response && (
          <span className="shrink-0 text-1 text-muted">
            <i className="bi bi-paperclip" aria-hidden="true" /> respuesta
          </span>
        )}
        <InsigniaDeEstado solicitud={solicitud} />
      </button>
    </Card>
  );
}
